// @flow
import React, { Component, Fragment } from 'react';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import classNames from 'classnames/bind';
import Loading from '../../../../../elements/Loading/Loading';

import commonStyle from '../../../components/OrderCommon.scss';
import boxStyle from '../../../../../assets/styles/box.scss';
import style from './Completed.scss';
import buttonStyle from '../../../../../assets/styles/button.scss';
import formStyle from '../../../../../assets/styles/form.scss';
import paymentDoneImg from '../../../../../assets/img/order/manito-dinero-01.png';

import type { Node } from 'react';
import type { Order, Payout } from '../../../../../entities';

type Props = {
  +disbursementInfo?: Payout;
  +isFetchingDisbursement?: boolean;
  +order: Order;
  +buildSummary: () => Node;
  +buildPurchaserInfo: () => Node;
  +fetchDisbursementInfo: () => void;
  +buildProducts: () => Node;
};

export default class Completed extends Component<Props> {

  componentDidMount() {
    const { fetchDisbursementInfo } = this.props;
    fetchDisbursementInfo();
  }

  getDisbursementInfo(method: string): ?{ [string]: string } {
    const { disbursementInfo } = this.props;
    if (!disbursementInfo) return;
    switch (method) {
      case 'BANK_ACCOUNT':
        return {
          account: (disbursementInfo && disbursementInfo.bank) ? disbursementInfo.bank.name : '',
          accountNumber: disbursementInfo ? disbursementInfo.accountNumber : '',
        };
      case 'PAYPAL':
        return {
          account: 'Paypal',
          paypalEmail: disbursementInfo ? disbursementInfo.paypalEmail : '',
        };
      default:
        return;
    }
  }

  getTopContent = (): Node => {
    const { order } = this.props;
    const pendingPayed = <Fragment>
      <FormattedMessage id="order.sale.completed.pendingPayment.title">
        {txt => <h2 className={classNames(boxStyle.boxTitle, boxStyle.smallSpace, style.title)}>{txt}</h2>}
      </FormattedMessage>
      <FormattedMessage id="order.sale.completed.pendingPayment.message.first">
        {txt => <FormattedMessage id="order.sale.completed.pendingPayment.message.second">{
          txt2 => <p className={style.text}>{txt}<br/>{txt2}</p>
        }</FormattedMessage>}
      </FormattedMessage>
    </Fragment>;
    const alreadyPayed = <Fragment>
      <img src={paymentDoneImg}
           className={style.paymentDoneImg}
           alt="Payment done"/>
      <FormattedMessage id="order.sale.completed.paymentDone.title">
        {txt => <h2 className={
          classNames(boxStyle.boxTitle, boxStyle.smallSpace, style.title, style.paymentDone)}>{txt}</h2>}
      </FormattedMessage>
      <FormattedMessage id="order.sale.completed.paymentDone.message">
        {txt => <p className={style.text}>{txt}</p>}
      </FormattedMessage>
    </Fragment>;
    return order.hasPaidDisbursement ? alreadyPayed : pendingPayed;
  };

  getBottomContent = (): Node => {
    const { isFetchingDisbursement, disbursementInfo, order } = this.props;

    const noPayout = !disbursementInfo && <Fragment>
      <FormattedMessage id="order.sale.completed.disbursement.noAccount.message">
        {txt => <p className={style.text}>{txt}</p>}
      </FormattedMessage>
      <FormattedMessage id="order.sale.completed.disbursement.noAccount.button">{
        txt => <a href="/preferences/payouts"
                  className={classNames(buttonStyle.btn, buttonStyle.orange, style.button)}
                  target="_blank">{txt}</a>
      }</FormattedMessage>
    </Fragment>;

    const disbursement = !!disbursementInfo && this.getDisbursementInfo(disbursementInfo.method);
    const payoutInfo = !!disbursementInfo && <Fragment>
      <section className={classNames(formStyle.formWrap)}>
        {disbursement &&
        <section className={classNames(formStyle.row, formStyle.inputRow, formStyle.dualWrap, formStyle.responsive)}>
          <article className={formStyle.col}>
            <FormattedMessage id="order.sale.completed.disbursement.account">{
              txt => (<label className={formStyle.label}>{txt}</label>)
            }</FormattedMessage>
            <p>{disbursement.account}</p>
          </article>
          {disbursement.account === 'Paypal' ?
            <article className={formStyle.col}>
              <FormattedMessage id="order.sale.completed.disbursement.paypalEmail">{
                txt => (<label className={formStyle.label}>{txt}</label>)
              }</FormattedMessage>
              <p>{disbursement.paypalEmail}</p>
            </article> :
            <article className={formStyle.col}>
              <FormattedMessage id="order.sale.completed.disbursement.accountNumber">{
                txt => (<label className={formStyle.label}>{txt}</label>)
              }</FormattedMessage>
              <p>{disbursement.accountNumber}</p>
            </article>}
        </section>}
        {!order.hasPaidDisbursement && <FormattedMessage id="order.sale.completed.disbursement.editAccount.button">{
          txt => <a href="/preferences/payouts"
                    className={classNames(buttonStyle.btn, buttonStyle.orange, style.button)}
                    target="_blank">{txt}</a>
        }</FormattedMessage>}
      </section>
    </Fragment>;
    return (
      <Fragment>
        <FormattedMessage id="order.sale.completed.disbursement.title">
          {txt => <h2 className={classNames(boxStyle.boxTitle, boxStyle.smallSpace, style.title)}>{txt}</h2>}
        </FormattedMessage>
        {isFetchingDisbursement &&
        <Loading type="circle"
                 color="orange"
                 className={style.loading}/>}
        {!isFetchingDisbursement && noPayout}
        {!isFetchingDisbursement && payoutInfo}
      </Fragment>
    );
  };

  render() {
    const { buildSummary, buildPurchaserInfo, buildProducts, order } = this.props;
    return <main className={commonStyle.mainWrap}>
      <section className={classNames(commonStyle.wrap, commonStyle.leftWrap)}>
        <section className={classNames(boxStyle.box, style.confirmation)}>
          {this.getTopContent()}
          {/*!order.hasPaidDisbursement && */<hr className={style.line}/>}
          {/*!order.hasPaidDisbursement && */this.getBottomContent()}
        </section>
        {buildProducts()}
      </section>
      <section className={classNames(commonStyle.wrap, commonStyle.rightWrap)}>
        {buildPurchaserInfo()}
        {buildSummary()}
      </section>
    </main>
  }
}

Completed.propTypes = {
  disbursementInfo: PropTypes.object,
  isFetchingDisbursement: PropTypes.bool,
  order: PropTypes.object.isRequired,
  buildSummary: PropTypes.func.isRequired,
  buildPurchaserInfo: PropTypes.func.isRequired,
  fetchDisbursementInfo: PropTypes.func.isRequired,
  buildProducts: PropTypes.func.isRequired,
};



// WEBPACK FOOTER //
// ./src/modules/order/Sale/completed/components/Completed.jsx